import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthSplitLayout } from '../components/AuthSplitLayout.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { adminApi, ApiError } from '../lib/api.ts';
import { setAdminAccessToken } from '../lib/auth/adminToken.ts';
import { readDenyReason } from '../lib/auth/denyReason.ts';
import { getAdminSupabase } from '../lib/supabase.ts';
import { parseAuthErrorFromUrl } from '../lib/authErrorFromUrl.ts';

// Landing point for the central l2u-identity return. The admin Supabase client
// sets detectSessionInUrl: false on purpose (lib/supabase.ts) — so this route
// picks the session out of the URL itself:
//   - PKCE (the pinned flow): ?code=
//   - implicit shape: #access_token & #refresh_token, kept as a fallback
//     because both shapes are observed on this project's auth links.
// After establishing the session it confirms admin membership via
// /api/auth/admin/me so an un-added SSO user gets a clear message instead of a
// silent sign-out.
export function SsoCallback() {
  const navigate = useNavigate();
  const [notice, setNotice] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    // Guard against React strict-mode double-invocation: exchanging the same
    // authorization code twice fails the second time.
    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      // IdP / OAuth errors ride back on the return URL instead of tokens.
      const authError = parseAuthErrorFromUrl(window.location.search, window.location.hash);
      if (authError) {
        window.history.replaceState(null, '', window.location.pathname);
        setNotice(authError.description ?? 'Sign-in was cancelled or failed. Please try again.');
        return;
      }

      const supabase = getAdminSupabase();
      const hash = window.location.hash.replace(/^#/, '');
      const fragment = hash ? new URLSearchParams(hash) : null;
      const query = new URLSearchParams(window.location.search);
      const access = fragment?.get('access_token');
      const refresh = fragment?.get('refresh_token');
      const code = query.get('code');

      if (code) {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) throw error;
        setAdminAccessToken(data.session?.access_token ?? null);
      } else if (access && refresh) {
        const { error } = await supabase.auth.setSession({
          access_token: access,
          refresh_token: refresh,
        });
        if (error) throw error;
        setAdminAccessToken(access);
      } else {
        setNotice('This sign-in link is missing its credentials. Please try again.');
        return;
      }

      // Strip credentials from the URL before any further navigation.
      window.history.replaceState(null, '', window.location.pathname);

      // Confirm this SSO user maps to an admin. On first login the API attempts
      // the guarded email backfill; refusals come back as 403 with a machine
      // reason code in `details`.
      try {
        await adminApi.get('/api/auth/admin/me');
      } catch (err) {
        // 403: authenticated but refused. The session is deliberately KEPT —
        // clearing it here would end only THIS portal's session while the
        // central l2u-identity and Okta sessions survive, so the next sign-in
        // click would round-trip silently and refuse again (spec S10). Hand off
        // to /admin/no-access, which owns the wording and offers a sign-out
        // that ends the central session too. The reason rides in history state
        // so that page's first paint is already correct.
        if (err instanceof ApiError && err.status === 403) {
          navigate('/admin/no-access', {
            replace: true,
            state: { reason: readDenyReason(err.details) },
          });
          return;
        }
        if (err instanceof ApiError && err.status === 401) {
          // The session we just established is not usable at all — there is
          // nothing to preserve, so clear it rather than leaving the user half
          // signed in. Deliberately says nothing about authorisation: that
          // wording belongs to AdminNoAccess alone.
          try {
            await supabase.auth.signOut();
          } catch {
            // ignore
          }
          setAdminAccessToken(null);
          setNotice('We could not complete sign-in on this portal. Please try again.');
          return;
        }
        throw err;
      }

      navigate('/admin', { replace: true });
    };

    void run().catch(() => {
      setNotice('Sign-in failed. Please try again.');
    });
  }, [navigate]);

  return (
    <AuthSplitLayout variant="admin">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl text-slate-900">Signing you in</h2>
          <p className="text-sm text-slate-500">Completing sign-in…</p>
        </div>
        {notice ? (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
              {notice}
            </div>
            <Link
              to="/admin/login"
              className="text-sm font-medium text-brand-primary hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <CenteredSpinner />
        )}
      </div>
    </AuthSplitLayout>
  );
}
